import {
  CONTRACT_FINANCING_CONTINGENCY,
  CONTRACT_FINANCING_CONTINGENCY_TYPES,
} from '@/config/constants';
import { formatContractDate } from '@/utils/contractFormat';

const TYPE_TEXT: Partial<Record<string, string>> = {
  [CONTRACT_FINANCING_CONTINGENCY_TYPES.MORTGAGE_FINANCING]:
    'mortgage financing',
  [CONTRACT_FINANCING_CONTINGENCY_TYPES.SELLER_FINANCING]: 'seller financing',
  [CONTRACT_FINANCING_CONTINGENCY_TYPES.LOAN_ASSUMPTION]:
    "approval to assume Seller's existing loan",
  [CONTRACT_FINANCING_CONTINGENCY_TYPES.COMMERCIAL]: 'commercial financing',
};

export type FinancingContingencyKind = 'none' | 'purchasePrice' | 'timeline';

/** Normalizes stored values; the old "selected" placeholder means the common case. */
export function financingContingencyKind(
  value?: string | null
): FinancingContingencyKind {
  if (value === CONTRACT_FINANCING_CONTINGENCY.TIMELINE) return 'timeline';
  if (
    value === CONTRACT_FINANCING_CONTINGENCY.AT_LEAST_PURCHASE_PRICE ||
    value === CONTRACT_FINANCING_CONTINGENCY.SELECTED
  ) {
    return 'purchasePrice';
  }
  return 'none';
}

/**
 * Financing-contingency paragraph for financed offers, shared by the contract
 * preview and the PDF. Previously the contract said "This sale is contingent
 * upon Buyer obtaining mortgage financing" no matter what the buyer chose.
 *
 * - purchasePrice: financing sufficient to buy at the Purchase Price (tied to
 *   the appraisal); buyer may terminate with deposit returned if not obtained
 *   by the Financing Commitment Date.
 * - timeline: an expiring contingency — after the Financing Commitment Date it
 *   is deemed satisfied and no longer protects the deposit.
 */
export function financingContingencyText(opts: {
  contingency?: string | null;
  contingencyType?: string | null;
  commitmentDate?: string | null;
}): string {
  const kind = financingContingencyKind(opts.contingency);
  const what =
    (opts.contingencyType && TYPE_TEXT[opts.contingencyType]) ||
    'mortgage financing';
  const date = formatContractDate(opts.commitmentDate);

  if (kind === 'none') {
    return `This Agreement is not contingent upon Buyer obtaining financing. Buyer will deliver the lender's written approval, whether conditional or outright, to Seller no later than ${date}, but Buyer's obligation to close is not conditioned on obtaining it.`;
  }
  if (kind === 'timeline') {
    return `This Agreement is contingent upon Buyer obtaining a written commitment for ${what} on or before ${date} (the "Financing Deadline"). If Buyer has not obtained the commitment, Buyer may terminate this Agreement by written notice to Seller delivered on or before the Financing Deadline, and the earnest money shall be returned to Buyer. If Buyer does not deliver that notice by the Financing Deadline, this financing contingency expires and is deemed satisfied, and Buyer's later failure to obtain financing will not entitle Buyer to a return of the earnest money.`;
  }
  return `This Agreement is contingent upon Buyer obtaining a written commitment for ${what} in an amount sufficient to purchase the Property at the Purchase Price, which commitment may be conditioned on the Property appraising for at least the Purchase Price. Buyer will deliver the commitment to Seller no later than ${date}. If Buyer, after diligent good-faith effort, cannot obtain the commitment by that date, Buyer may terminate this Agreement by written notice to Seller delivered on or before that date, and the earnest money shall be returned to Buyer.`;
}
